import {
  ALL_INVOICE_PAGE_SIZES,
  DEFAULT_INVOICE_ASSET_FORMAT,
  type InvoicePageSizeId,
  type InvoiceRow,
} from "@bizy/core";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/field";
import { useRegisterInvoiceAsset, useShareInvoice, useVoidInvoice } from "./api";
import { downloadBlob, invoiceAssetFilename, renderInvoiceImage, uploadInvoiceAsset } from "./render";
import { getDictionary } from "@bizy/core/i18n/resolve";
import { usePreferences } from "@/providers/preferences-provider";
import { Download, Printer, Share2, XCircle } from "lucide-react";
import { useState, type RefObject } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export interface InvoiceActionsProps {
  workspaceId: string;
  workspaceSlug: string;
  invoice: InvoiceRow;
  documentRef: RefObject<HTMLDivElement | null>;
  onPageSizeChange: (pageSize: InvoicePageSizeId) => void;
}

/**
 * Output actions for an invoice (PRD §3.2.4): page-size aware print preview,
 * PNG download, storage upload and WhatsApp/email sharing.
 */
export const InvoiceActions = ({
  workspaceId,
  workspaceSlug,
  invoice,
  documentRef,
  onPageSizeChange,
}: InvoiceActionsProps) => {
  const { locale } = usePreferences();
  const dictionary = getDictionary(locale);
  const navigate = useNavigate();
  const registerAsset = useRegisterInvoiceAsset(workspaceId);
  const shareInvoice = useShareInvoice(workspaceId);
  const voidInvoice = useVoidInvoice(workspaceId);
  const [busy, setBusy] = useState<"download" | "share" | null>(null);

  const rasterise = async (): Promise<Blob> => {
    const element = documentRef.current;
    if (!element) throw new Error("Invoice preview is not ready yet.");
    return renderInvoiceImage({ element, format: DEFAULT_INVOICE_ASSET_FORMAT });
  };

  const onDownload = async () => {
    setBusy("download");
    try {
      const blob = await rasterise();
      downloadBlob(blob, invoiceAssetFilename(invoice.number));
      const uploaded = await uploadInvoiceAsset({
        workspaceId,
        invoiceId: invoice.id,
        pageSize: invoice.page_size,
        format: DEFAULT_INVOICE_ASSET_FORMAT,
        blob,
      });
      await registerAsset.mutateAsync({
        invoiceId: invoice.id,
        assetKey: uploaded.key,
        mime: uploaded.mime,
        bytes: uploaded.bytes,
        pageSize: invoice.page_size,
        format: DEFAULT_INVOICE_ASSET_FORMAT,
      });
      toast.success("Invoice image saved.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not render the invoice.");
    } finally {
      setBusy(null);
    }
  };

  const onShare = async (channel: "whatsapp" | "email") => {
    setBusy("share");
    try {
      // Ensure an asset exists before handing a link to the customer.
      if (!invoice.asset_key) {
        const blob = await rasterise();
        const uploaded = await uploadInvoiceAsset({
          workspaceId,
          invoiceId: invoice.id,
          pageSize: invoice.page_size,
          format: DEFAULT_INVOICE_ASSET_FORMAT,
          blob,
        });
        await registerAsset.mutateAsync({
          invoiceId: invoice.id,
          assetKey: uploaded.key,
          mime: uploaded.mime,
          bytes: uploaded.bytes,
          pageSize: invoice.page_size,
          format: DEFAULT_INVOICE_ASSET_FORMAT,
        });
      }

      const links = await shareInvoice.mutateAsync({ invoiceId: invoice.id, channel, locale });
      const target = channel === "whatsapp" ? links.whatsapp_url : links.email_url;
      window.open(target, "_blank", "noopener,noreferrer");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not prepare the share link.");
    } finally {
      setBusy(null);
    }
  };

  const onVoid = async () => {
    try {
      await voidInvoice.mutateAsync({ invoiceId: invoice.id });
      toast.success("Invoice voided.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not void the invoice.");
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        aria-label={dictionary.invoice.pageSize}
        value={invoice.page_size}
        onChange={(event) => onPageSizeChange(event.target.value as InvoicePageSizeId)}
        className="w-44"
      >
        {ALL_INVOICE_PAGE_SIZES.map((size) => (
          <option key={size.id} value={size.id}>
            {size.label}
          </option>
        ))}
      </Select>

      <Button
        variant="outline"
        size="sm"
        onClick={() => navigate(`/w/${workspaceSlug}/invoices/${invoice.id}/print`)}
      >
        <Printer className="size-4" aria-hidden />
        {dictionary.invoice.print}
      </Button>

      <Button
        variant="outline"
        size="sm"
        loading={busy === "download"}
        disabled={busy === "share"}
        onClick={() => void onDownload()}
      >
        {busy === "download" ? null : <Download className="size-4" aria-hidden />}
        {dictionary.invoice.download}
      </Button>

      <Button
        size="sm"
        loading={busy === "share"}
        disabled={busy === "download"}
        onClick={() => void onShare("whatsapp")}
      >
        {busy === "share" ? null : <Share2 className="size-4" aria-hidden />}
        {dictionary.invoice.shareWhatsApp}
      </Button>

      <Button variant="ghost" size="sm" disabled={busy !== null} onClick={() => void onShare("email")}>
        {dictionary.invoice.shareEmail}
      </Button>

      <Button variant="ghost" size="sm" className="text-danger" loading={voidInvoice.isPending} onClick={() => void onVoid()}>
        <XCircle className="size-4" aria-hidden />
        Void
      </Button>
    </div>
  );
};
